<!-- @format -->

<template>
    <div
        ref="hotelMarker"
        tabindex="0"
        @click.prevent="$emit('click', $event)"
        @keypress.enter="$emit('click', $event)"
        @mouseover="handleMouseOver"
        @mouseleave="handleMouseLeave">
        <div class="hotel-marker-container">
            <BaseImage
                v-if="campaignIconUrl"
                :src="isHovered ? campaignIconHoverUrl : campaignIconUrl"
                class="gst-campaign-icon" />
            <span>{{ hotelMarkerPrice }}</span>
        </div>
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { create as CreateBundleProductsModel } from '@tenant/app/modelsV2/BundleProductsModel';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin';

export default {
    name: 'MapMarkerHotel',
    mixins: [EventFlowMixin],
    props: {
        mapsApi: {
            type: Object,
            required: true,
        },
        map: {
            type: Object,
            required: true,
        },
        hotel: {
            type: Object,
            required: true,
        },
        isActive: {
            type: Boolean,
            default: false,
        },
        isFocused: {
            type: Boolean,
            default: false,
        },
        bundleProducts: {
            type: Object,
            required: true,
            default: function () {
                return CreateBundleProductsModel();
            },
        },
    },
    data() {
        return {
            popupInstance: null,
            styleElement: null,
            isHovered: false,
        };
    },
    computed: {
        ...mapGetters({
            redeemPointsToggle: 'appState/getRedeemPointsToggle',
        }),
        bundleProductsTotals() {
            return this.bundleProducts.getTotals();
        },
        basePrice() {
            if (this.showBasePriceWithoutDiscount) {
                return this.$options.filters.currencyFilter(
                    Math.ceil(this.bundleProductsTotals.basePrice),
                    this.hotel.selectedRoom.selectedRate.currency,
                    { digits: 0 }
                );
            } else {
                return this.$options.filters.currencyFilter(
                    this.bundleProductsTotals.basePrice,
                    this.hotel.selectedRoom.selectedRate.currency
                );
            }
        },
        showBasePriceWithoutDiscount() {
            return (
                this.bundleProductsTotals.basePrice !==
                this.bundleProductsTotals.basePriceWithoutDiscount
            );
        },
        roundedUpTotalPriceFormatted() {
            return this.$options.filters.currencyFilter(
                Math.ceil(this.bundleProductsTotals.totalPrice),
                this.selectedRate.currency,
                { digits: 0 }
            );
        },
        pricePerNight() {
            return this.$options.filters.currencyFilter(
                Math.ceil(this.selectedRate.pricePerNight),
                this.selectedRate.currency,
                { digits: 0 }
            );
        },
        selectedRate() {
            return this.hotel.selectedRoom.selectedRate;
        },
        hotelMarkerPrice() {
            return this.redeemPointsToggle
                ? this.$t('shared:common.pointWithCount', {
                      count: this.hotel.redeemPoints,
                      formattedCount: this.$options.filters.bonusPoints(this.hotel.redeemPoints),
                  })
                : this.hotel.allInclusivePricing && this.isHotelOnlyFlow
                  ? this.roundedUpTotalPriceFormatted
                  : this.selectedRate?.pricePerNight
                    ? this.pricePerNight
                    : this.basePrice;
        },
        campaignIconUrl() {
            return this.hotel.campaign?.config?.iconUrl || null;
        },
        campaignIconHoverUrl() {
            return (
                this.hotel.campaign?.config?.iconHoverUrl ||
                this.hotel.campaign?.config?.iconUrl ||
                null
            );
        },
    },
    watch: {
        isFocused: function (newValue) {
            if (this.popupInstance) {
                if (newValue) {
                    this.popupInstance.addClassName('gst-map-marker-hotel--focused');
                    this.isHovered = true;
                } else {
                    this.popupInstance.removeClassName('gst-map-marker-hotel--focused');
                    this.isHovered = false;
                }
            }
        },
        isActive: function (newValue) {
            if (this.popupInstance) {
                if (newValue) {
                    this.popupInstance.addClassName('gst-map-marker-hotel--active');
                    this.isHovered = true;
                } else {
                    this.popupInstance.removeClassName('gst-map-marker-hotel--active');
                    this.isHovered = false;
                }
            }
        },
    },
    methods: {
        injectDynamicStyles() {
            const lightTheme = this.$vuetify.theme.themes.light;
            const backgroundColor = this.hotel.campaign?.type
                ? this.hotel.campaign.config?.backgroundColor || lightTheme.defaultPromotedBg
                : '';
            const fontColor = this.hotel.campaign?.type
                ? this.hotel.campaign.config?.fontColor || lightTheme.defaultPromotedColor
                : '';
            if (backgroundColor && fontColor) {
                this.styleElement = document.createElement('style');
                this.styleElement.type = 'text/css';
                const customClass = `.gst-map-marker-hotel--campaign-${this.hotel.id}`;

                this.styleElement.textContent = `
                        ${customClass} .mapboxgl-popup-content {
                            border-color: ${backgroundColor};
                            background-color: ${backgroundColor};
                            color: ${fontColor};
                            padding: 0;
                        }
                        ${customClass}.gst-map-marker-hotel .mapboxgl-popup-content .hotel-marker-container {
                            padding: 0 8px;
                        }
                        ${customClass} .mapboxgl-popup-tip {
                            border-top-color: ${backgroundColor};
                        }
                        ${customClass}:hover .mapboxgl-popup-content,
                        ${customClass}.gst-map-marker-hotel--active .mapboxgl-popup-content,
                        ${customClass}.gst-map-marker-hotel--focused .mapboxgl-popup-content {
                            color: ${backgroundColor};
                            background-color: ${lightTheme.favoriteUnMarked};
                            border-color: ${backgroundColor};
                        }
                    `;

                document.head.appendChild(this.styleElement);
            }
        },
        removeDynamicStyles() {
            if (this.styleElement) {
                document.head.removeChild(this.styleElement);
                this.styleElement = null;
            }
        },
        handleMouseOver() {
            if (!this.isActive) {
                this.isHovered = true;
            }
        },
        handleMouseLeave() {
            if (!this.isActive) {
                this.isHovered = false;
            }
        },
    },
    mounted() {
        this.injectDynamicStyles();
        this.popupInstance = new this.mapsApi.Popup({
            anchor: 'bottom',
            closeButton: false,
            closeOnClick: false,
            className: `gst-map-marker-hotel gst-map-marker-hotel--campaign-${this.hotel.id}`,
        })
            .setLngLat([this.hotel.longitude, this.hotel.latitude])
            .setDOMContent(this.$refs.hotelMarker)
            .addTo(this.map);
    },
    beforeDestroy() {
        if (this.popupInstance) {
            this.popupInstance.remove();
        }
        this.removeDynamicStyles();
    },
};
</script>

<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';

.gst-campaign-icon {
    height: 19.2px;
    margin: 3.2px 0;
    max-width: 24.8px;
}

.gst-map-marker-hotel {
    z-index: $z-index-map-hotel-marker;

    .mapboxgl-popup-content {
        padding: 0 5px;
        border: 1px solid theme-color('primary');
        background-color: theme-color('primary');
        color: theme-color('white');
        font-size: font-size('s');
        font-weight: font-weight('large');
        font-family: $font-family;
        border-radius: 15px;
        cursor: pointer;

        .hotel-marker-container {
            display: flex;
            width: 100%;
            align-items: center;
            gap: 4px;
            cursor: pointer;
        }
    }

    .mapboxgl-popup-tip {
        position: relative;
        top: -1px;
        border: 6px solid transparent;
        border-top-color: theme-color('primary');

        &::before {
            position: absolute;
            top: -6px;
            left: -5px;
            height: 0;
            width: 0;
            border: 5px solid transparent;
            border-bottom: none;
            content: '';
            z-index: $z-index-map-hotel-marker;
            box-shadow: 0 7px 4px rgba(0, 0, 0, 0.2);
        }
    }
}

.gst-map-marker-hotel:hover,
.gst-map-marker-hotel--active,
.gst-map-marker-hotel--focused {
    z-index: $z-index-map-hotel-marker + 1;

    .mapboxgl-popup-content {
        border: 1px solid theme-color('primary');
        background-color: theme-color('white');
        color: theme-color('primary');
    }

    .mapboxgl-popup-tip::before {
        border-top-color: theme-color('white');
    }
}
</style>
